import dayjs from "dayjs";
import IMAGES from "../constants/images";
import moment from "moment";

const convertToShortTimeFormat = (str) => {
  return str.replace(/Hour/g, "Hr").replace(/Minutes/g, "Min");
};

const checkFiveMinBeforeTime = (sTime) => {
  const targetTime = new Date(sTime); // Target time
  const currentTime = new Date(); // Current system time

  const diffInMinutes =
    (currentTime.getTime() - targetTime.getTime()) / (1000 * 60); // Convert ms to minutes

  if (diffInMinutes >= -5 && diffInMinutes <= 250) {
    return true;
  } else {
    return false;
  }
};

const getRemainingTime = (targetDateTime) => {
  const now = dayjs();
  const target = dayjs(targetDateTime);

  if (target.isBefore(now)) {
    return "Started";
  }

  const diffInMinutes = target.diff(now, "minute");

  if (diffInMinutes < 60) {
    return `${diffInMinutes} min left`;
  }

  const diffInHours = target.diff(now, "hour");
  if (diffInHours < 24) {
    return `${diffInHours} hours left`;
  }

  const diffInDays = target.diff(now, "day");
  if (diffInDays === 1) {
    return `${diffInDays} day left`;
  } else {
    return `${diffInDays} days left`;
  }
};

const getModeIcon = (mode) => {
  if (mode === "AUDIO") {
    return IMAGES.audio_call;
  } else if (mode === "VIDEO") {
    return IMAGES.video_icon;
  } else if (mode === "INPERSON") {
    return IMAGES.in_person;
  } else {
    return IMAGES.chat;
  }
};

const getTopMode = (data) => {
  if (!Array.isArray(data) || data.length === 0) {
    return "No_Mode";
  }

  const modeLabels = {
    video: "Video",
    chat: "Chat",
    audio: "Audio",
    inPerson: "In-person",
  };

  const totals = data.reduce(
    (acc, curr) => {
      acc.video += curr.video || 0;
      acc.chat += curr.chat || 0;
      acc.audio += curr.audio || 0;
      acc.inPerson += curr.inPerson || 0;
      return acc;
    },
    { video: 0, chat: 0, audio: 0, inPerson: 0 }
  );

  const totalSum = Object.values(totals).reduce((sum, val) => sum + val, 0);

  if (totalSum === 0) {
    return "No_Mode";
  }

  const topCategoryKey = Object.keys(totals).reduce((a, b) =>
    totals[a] > totals[b] ? a : b
  );

  return modeLabels[topCategoryKey];
};

const isAllZeroOrNull = (data) => {
  return (
    data.week === null &&
    (data.month === null || data.month.every((item) => item.value === 0)) &&
    data.threeMonths === null
  );
};

const formatTimeRange = (startTime, endTime) => {
  // Helper function to format a time string into AM/PM format
  const formatToAMPM = (time) => {
    const [hours, minutes] = time.split(":").map(Number);

    // Create a new Date object to properly handle time
    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);

    // Get 12-hour format time with AM/PM
    const options = { hour: "2-digit", minute: "2-digit", hour12: true };
    const formattedTime = date.toLocaleTimeString([], options);

    // Split formattedTime into hour, minute and AM/PM parts
    const [formattedHourMinute, period] = formattedTime.split(" ");
    let [hour, minute] = formattedHourMinute.split(":");

    // Adjust hour to 12-hour format with zero padding (e.g., 03 instead of 3)
    hour = parseInt(hour) % 12 === 0 ? 12 : parseInt(hour) % 12;
    minute = minute.length === 1 ? `0${minute}` : minute; // Ensure 2 digits for minutes

    return { time: `${hour}:${minute}`, period };
  };

  // Format both start and end times into AM/PM format
  const startFormatted = formatToAMPM(startTime);
  const endFormatted = formatToAMPM(endTime);

  // Return the formatted time range in "03:00 - 03:30 PM" style
  return `${startFormatted.time} - ${endFormatted.time} ${endFormatted.period}`;
};
const formatAppointmentType = (type) => {
  // Handle camel case by adding hyphens and capitalizing
  let formattedType = type.replace(/([a-z])([A-Z])/g, "$1-$2"); // Add hyphen between lowercase and uppercase letters
  formattedType = formattedType.replace(/([A-Z])/g, (match) =>
    match.toLowerCase()
  ); // Convert to lowercase
  formattedType = formattedType.replace(/^./, (match) => match.toUpperCase()); // Capitalize the first letter

  return formattedType;
};

const getRiskColor = (risk) => {
  if (risk === "HIGH_RISK" || risk === "HIGH") {
    return "bg-[#DC2626]";
  } else if (risk === "MODERATE_RISK" || risk === "MODERATE") {
    return "bg-[#FF7439]";
  } else if (risk === "LOW_RISK" || risk === "LOW") {
    return "bg-[#FFEE33]";
  } else if (risk === "NO_RISK" || risk === "NO RISK") {
    return "bg-[#B7E049]";
  } else {
    return "bg-[#B2BEB5]"; // Gray for N/A or null
  }
};

const getRiskTextColor = (risk) => {
  if (risk === "N/A" || risk === null || risk === undefined || risk === "NO_RISK" || risk === "NO RISK" || risk === "LOW_RISK" || risk === "LOW") {
    return "text-[#374151]"; // Dark grey text for better contrast on light backgrounds
  } else {
    return "text-[#FFFFFF]";
  }
};

// Get the effective status to display (handles currentStatus for SCHEDULED appointments)
const getEffectiveStatus = (status, currentStatus) => {
  if (status === "SCHEDULED" && currentStatus) {
    return currentStatus;
  }
  return status;
};

const getStatusColor = (status, currentStatus = null) => {
  const effectiveStatus = getEffectiveStatus(status, currentStatus);

  if (effectiveStatus === "SCHEDULED" || effectiveStatus === "Upcoming" || effectiveStatus === "UPCOMING") {
    return "bg-[#FFE375]";
  } else if (effectiveStatus === "COMPLETED" || effectiveStatus === "COMPLETE" || effectiveStatus === "Completed") {
    return "bg-[#98FF8A]";
  } else if (effectiveStatus === "PENDING") {
    return "bg-[#FFAF77]";
  } else if (effectiveStatus === "CANCELED" || effectiveStatus === "CANCELLED") {
    return "bg-[#FF7439]";
  } else if (effectiveStatus === "EXPIRED") {
    return "bg-[#DC2626]";
  } else if (effectiveStatus === "IN_COMPLETE" || effectiveStatus === "INCOMPLETE") {
    return "bg-[#DC2626]";
  } else if (effectiveStatus === "IN_PROGRESS") {
    return "bg-[#DC2626]";
  } else if (effectiveStatus === "COUNSELOR_UNAVAILABLE" || effectiveStatus === "COUNSELLOR UNAVAILABLE") {
    return "bg-[#FF7439]";
  } else if (effectiveStatus === "MISSED" || effectiveStatus === "Missed" || effectiveStatus === "COUNSELLOR MISSED" || effectiveStatus === "COUNSELLOR_MISSED" || effectiveStatus === "STUDENT MISSED" || effectiveStatus === "STUDENT_MISSED" || effectiveStatus === "MISSED BY BOTH" || effectiveStatus === "MISSED_BY_BOTH") {
    return "bg-[#DC2626]";
  } else if (effectiveStatus === "RISK MISSING") {
    return "bg-[#DC2626]"; // Red for risk missing
  } else {
    return "bg-[#4B61FF]"; // Default blue
  }
};

const getStatusText = (status, currentStatus = null) => {
  const effectiveStatus = getEffectiveStatus(status, currentStatus);

  if (effectiveStatus === "SCHEDULED" || effectiveStatus === "Upcoming" || effectiveStatus === "UPCOMING") {
    return "Upcoming";
  } else if (effectiveStatus === "COMPLETE" || effectiveStatus === "COMPLETED") {
    return "Completed";
  } else if (effectiveStatus === "PENDING") {
    return "Pending";
  } else if (effectiveStatus === "CANCELED") {
    return "Cancelled";
  } else if (effectiveStatus === "EXPIRED") {
    return "Expired";
  } else if (effectiveStatus === "IN_COMPLETE" || effectiveStatus === "INCOMPLETE") {
    return "In Complete";
  } else if (effectiveStatus === "IN_PROGRESS") {
    return "In Progress";
  } else if (effectiveStatus === "STUDENT MISSED" || effectiveStatus === "STUDENT_MISSED") {
    return "Student Missed";
  } else if (effectiveStatus === "COUNSELLOR MISSED" || effectiveStatus === "COUNSELLOR_MISSED") {
    return "Counselor Missed";
  } else if (effectiveStatus === "COUNSELOR_UNAVAILABLE" || effectiveStatus === "COUNSELLOR UNAVAILABLE") {
    return "Counselor Unavailable";
  } else if (effectiveStatus === "MISSED" || effectiveStatus === "Missed" || effectiveStatus === "MISSED BY COUNSELLOR") {
    return "Missed";
  } else if (effectiveStatus === "MISSED BY BOTH" || effectiveStatus === "MISSED_BY_BOTH") {
    return "Missed By Both";
  } else if (effectiveStatus === "RISK MISSING") {
    return "Risk Missing";
  } else if (effectiveStatus === "HIGH_RISK") {
    return "HIGH";
  } else if (effectiveStatus === "LOW_RISK") {
    return "LOW";
  } else if (effectiveStatus === "NO_RISK") {
    return "NO RISK";
  } else if (effectiveStatus === "MODERATE_RISK") {
    return "MODERATE";
  } else if (effectiveStatus === "N/A" || effectiveStatus === null || effectiveStatus === undefined) {
    return "N/A";
  } else {
    return effectiveStatus; // Return as-is if no mapping found
  }
};

const formatToAMPM = (time) => {
  if (typeof time !== "string") {
    console.warn("Invalid time format:", time); // helps you debug
    return "";
  }

  const [hour, minute, second] = time.split(":");
  return moment({ hour, minute, second }).format("h:mm A");
};

const stringToTime = (timeRangeString) => {
  if (typeof timeRangeString !== "string") return "";

  const timePart = timeRangeString.split(" (")[0]; // "09:00:00 - 22:00:00"
  const [startTime, endTime] = timePart.split(" - ").map((t) => t.trim());

  return `${formatToAMPM(startTime)}-${formatToAMPM(endTime)}`;
};

const stringToDuration = (string) => {
  const timeRangeString = string;

  const match = timeRangeString.match(/\(([^)]+)\)/); // Matches text inside ( )
  const duration = match ? match[1] : "";
  return duration;
};

const getWeekDateByDay = (dayName, referenceDate = new Date()) => {
  const days = [
    "SUNDAY",
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
  ];

  const targetDay = days.indexOf(dayName.toUpperCase());
  if (targetDay === -1) throw new Error("Invalid day name");

  const ref = new Date(referenceDate);
  const currentDay = ref.getDay();

  const diff = (targetDay - currentDay + 7) % 7;

  const targetDate = new Date(ref);
  targetDate.setDate(ref.getDate() + diff);

  return targetDate.toISOString().split("T")[0]; // Format: YYYY-MM-DD
};

export {
  convertToShortTimeFormat,
  checkFiveMinBeforeTime,
  getModeIcon,
  getRemainingTime,
  getTopMode,
  isAllZeroOrNull,
  formatTimeRange,
  formatAppointmentType,
  getRiskColor,
  getStatusColor,
  stringToTime,
  getStatusText,
  stringToDuration,
  getWeekDateByDay,
  getRiskTextColor,
  getEffectiveStatus,
};
